// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { squarify } from '../utils/squarify';
import { bySector, type Group } from '../analysis';
import { OVER, positionColour, sectorColour } from '../sectors';
import { escapeHtml, formatCompact, formatNumber, formatPercent } from '../utils/format';
import { glossaryTerm } from '../glossary';

const mt = (t: number) => `${(t / 1e6).toFixed(2)} Mt`;

export function renderSectors(root: HTMLElement, ctx: ViewContext): void {
  const { facilities } = ctx.data;
  const sectors = bySector(facilities);
  let selected = sectors[0].key;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Emissions by industry</h1>
      <p class="view-sub">
        Every covered facility grouped by its ${glossaryTerm('anzsic', 'industry')}. Rectangle area is covered emissions —
        coal mining and gas extraction fill most of the map. Click an industry to see its facilities and how many came in
        over their ${glossaryTerm('baseline', 'baselines')}.
      </p>
    </div>

    <div class="panel">
      <div class="panel-title">Covered emissions by industry</div>
      <div class="panel-sub">Area = tonnes CO₂-e. Hover for exact figures; click to select an industry below.</div>
      <div class="treemap-wrap" data-role="treemap" style="height:420px"></div>
    </div>

    <div class="grid-2 wide" style="margin-top:var(--space-lg)">
      <div class="panel">
        <div class="panel-title">All industries</div>
        <div class="panel-sub">Covered emissions, how many facilities, how many over baseline, and methane share. Click a row.</div>
        <div class="table-scroll" style="max-height:520px;overflow-y:auto">
          <table class="data">
            <thead><tr><th>Industry</th><th class="t-num">Covered</th><th class="t-num">Facilities</th><th class="t-num">Over</th><th class="t-num">Methane</th></tr></thead>
            <tbody data-role="rows"></tbody>
          </table>
        </div>
      </div>
      <div class="panel" data-role="detail"></div>
    </div>
  `;

  const treemapEl = root.querySelector('[data-role="treemap"]') as HTMLElement;
  const rowsEl = root.querySelector('[data-role="rows"]') as HTMLElement;
  const detailEl = root.querySelector('[data-role="detail"]') as HTMLElement;

  const drawTreemap = () => {
    const W = treemapEl.clientWidth || 900;
    const H = 420;
    const rects = squarify(sectors.map((s) => s.covered), W, H);
    treemapEl.innerHTML = rects
      .map((r, i) => {
        const s = sectors[i];
        if (r.w < 1 || r.h < 1) return '';
        const showLabel = r.w > 54 && r.h > 24;
        const tip = `${s.label}\n${mt(s.covered)} · ${s.count} facilities\n${s.nOver} over baseline · methane ${(s.methaneShare ?? 0).toFixed(0)}%`;
        return `<div class="tm-cell" data-key="${escapeHtml(s.key)}" style="left:${r.x}px;top:${r.y}px;width:${r.w}px;height:${r.h}px;background:${sectorColour(s.label)};${s.key === selected ? 'outline:3px solid #0f172a;outline-offset:-3px;z-index:2' : ''}" data-tip="${escapeHtml(tip)}">
          ${showLabel ? `<div class="tm-label">${escapeHtml(s.label)}<br>${formatCompact(s.covered)}</div>` : ''}
        </div>`;
      })
      .join('');
  };

  const drawRows = () => {
    rowsEl.innerHTML = sectors
      .map(
        (s) => `<tr data-key="${escapeHtml(s.key)}"${s.key === selected ? ' style="background:var(--bg-active)"' : ''}>
          <td><span class="legend-swatch" style="display:inline-block;background:${sectorColour(s.label)};margin-right:6px;vertical-align:middle"></span>${escapeHtml(s.label)}</td>
          <td class="t-num">${formatCompact(s.covered)}</td>
          <td class="t-num">${formatNumber(s.count)}</td>
          <td class="t-num" style="color:${s.nOver > s.count / 2 ? OVER : 'inherit'}">${formatNumber(s.nOver)}</td>
          <td class="t-num">${formatPercent(s.methaneShare ?? 0, 0)}</td>
        </tr>`,
      )
      .join('');
  };

  const drawDetail = () => {
    const s = sectors.find((x) => x.key === selected) as Group;
    detailEl.innerHTML = `
      <div class="panel-title">${escapeHtml(s.label)}</div>
      <div class="panel-sub">${mt(s.covered)} across ${s.count} facilities · ${s.nOver} over baseline · ${formatPercent(s.methaneShare ?? 0)} methane · net ${s.excess > 0 ? '+' : ''}${formatCompact(s.excess)} vs baseline</div>
      <div class="table-scroll" style="max-height:430px;overflow-y:auto">
        <table class="data">
          <thead><tr><th>Facility</th><th class="t-num">Covered</th><th class="t-num">vs base</th></tr></thead>
          <tbody>
            ${s.facilities
              .map(
                (f) => `<tr data-slug="${escapeHtml(f.slug)}">
                  <td class="loc-cell">${escapeHtml(f.name)}<br><span class="muted" style="font-size:var(--font-size-xs)">${escapeHtml(f.company)}${f.state !== 'MULTI' ? ` · ${f.state}` : ''}</span></td>
                  <td class="t-num">${formatCompact(f.covered)}</td>
                  <td class="t-num" style="color:${positionColour(f.excess)}">${f.excess > 0 ? '+' : ''}${formatCompact(f.excess)}</td>
                </tr>`,
              )
              .join('')}
          </tbody>
        </table>
      </div>`;
  };

  const select = (key: string) => {
    if (!sectors.some((s) => s.key === key)) return;
    selected = key;
    drawTreemap();
    drawRows();
    drawDetail();
  };

  treemapEl.addEventListener('click', (e) => {
    const cell = (e.target as Element).closest('[data-key]');
    if (cell) select(cell.getAttribute('data-key') as string);
  });
  rowsEl.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest('[data-key]');
    if (tr) select(tr.getAttribute('data-key') as string);
  });
  detailEl.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest('[data-slug]');
    if (tr) ctx.openFacility(tr.getAttribute('data-slug') as string);
  });

  drawTreemap();
  drawRows();
  drawDetail();
}
